/**
 * Billing Management Interfaces
 * สำหรับระบบจัดการวางบิลและบันทึกการชำระเงิน
 *
 * Base URL: /procurement/api/billing
 * Reference: BILLING_MANAGEMENT_API_GUIDE.md
 */

// ========== Query Keys ==========

export enum EBillingQueryKeys {
  GET_BILLING_ROUNDS = "GET_BILLING_ROUNDS",
  GET_BILLING_PERIODS = "GET_BILLING_PERIODS",
  GET_PERIOD_ROUNDS = "GET_PERIOD_ROUNDS",
  GET_BILLING_CUSTOMERS = "GET_BILLING_CUSTOMERS",
  GET_BILLING_INVOICES = "GET_BILLING_INVOICES",
  GET_BILLING_PAYMENTS = "GET_BILLING_PAYMENTS",
  GET_BILLING_PAYMENT_DETAIL = "GET_BILLING_PAYMENT_DETAIL",
  GET_CUSTOMERS_BILLING_SUMMARY = "GET_CUSTOMERS_BILLING_SUMMARY",
}

// ========== Billing Cycle Template Query Keys ==========

export enum EBillingCycleTemplateQueryKeys {
  GET_BILLING_CYCLE_TEMPLATES = "GET_BILLING_CYCLE_TEMPLATES",
  GET_BILLING_CYCLE_TEMPLATE_BY_UUID = "GET_BILLING_CYCLE_TEMPLATE_BY_UUID",
}

// ========== Billing Cycle Template ==========

export type BillingCycleType = "semi_monthly" | "weekly" | "monthly" | "custom";

export interface IBillingCycleTemplate {
  id: number;
  uuid: string;
  name: string;
  cycle_type: BillingCycleType;
  week_start_day: number | null; // 0=Sun … 6=Sat (weekly only)
  month_split_day: number | null; // 1-28 split day (semi_monthly only)
  month_billing_day: number | null; // 1-28 billing day (monthly only)
  description: string | null;
  franchise_uuid: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  removed_at: string | null;
}

export interface IBillingCycleSnapshot {
  cycle_type: BillingCycleType;
  week_start_day: number | null;
  month_split_day: number | null;
  month_billing_day: number | null;
}

export interface IBillingCycleTemplateQueryParams {
  search?: string;
  cycle_type?: BillingCycleType;
  is_active?: boolean;
}

export type IBillingCycleTemplatesResponse = IBillingCycleTemplate[];

export type IBillingCycleTemplateSingleResponse = IBillingCycleTemplate;

export interface ICreateBillingCycleTemplateDto {
  name: string;
  cycle_type: BillingCycleType;
  week_start_day?: number | null;
  month_split_day?: number | null;
  month_billing_day?: number | null;
  description?: string | null;
  is_active?: boolean;
}

export interface IUpdateBillingCycleTemplateDto extends Partial<ICreateBillingCycleTemplateDto> {}

export interface IUpdateCustomerBillingCycleDto {
  billing_cycle_template_uuid: string | null;
}

export interface IUpdateCustomerBillingCycleResponse {
  status: boolean;
  message: string;
  data: {
    uuid: string;
    customer_code: string;
    billing_cycle_template_uuid: string | null;
  };
}

// ========== Billing Round ==========

export interface IBillingRound {
  id: string;
  round_label: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  round_type: "global" | "per_customer";
  customer_code: string | null;
  billing_cycle_template_uuid: string | null;
  billing_cycle_type: BillingCycleType | null;
  billing_cycle_snapshot: IBillingCycleSnapshot | null;
  bill_count: number;
  branch_count: number;
  customer_count: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  status: "pending" | "completed";
  created_at: string;
  updated_at: string;
}

export interface IBillingRoundsQueryParams {
  page?: number;
  limit?: number;
  status?: "pending" | "completed";
  year?: number;
  search?: string;
  enforce_customer_scope?: boolean;
}

export interface IBillingRoundsSummary {
  total_rounds: number;
  pending_rounds: number;
  completed_rounds: number;
  total_outstanding: number;
}

export interface IBillingRoundsResponse {
  status: boolean;
  data: {
    rounds: IBillingRound[];
    summary: IBillingRoundsSummary;
    pagination: IPagination;
  };
}

// ========== Customer Billing Summary ==========

export interface IBillingCustomerInvoice {
  uuid: string;
  invoice_number: string;
  invoice_date: string;
  delivery_date?: string;
  due_date?: string;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  has_pending_payment?: boolean;
  pending_payment_uuid?: string | null;
  document_eligible?: boolean;
  delivery_completed?: boolean;
  document_blocked_reason?: string | null;
  invoice_confirmed?: boolean;
  payment_status: string; // 'pending' | 'partial' | 'paid'
  status?: string; // 'delivered' | 'confirmed' etc.
  branch_name?: string;
  branch_code?: string;
  order_uuid?: string;
}

export interface IBillingCustomer {
  customer_code: string;
  customer_name: string;
  customer_email: string;
  customer_folder_url: string | null;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  invoice_count: number;
  paid_invoice_count: number;
  billing_file_count: number;
  email_sent: boolean;
  email_sent_at: string | null;
  payment_status: "pending" | "partial" | "paid";
  billing_status: "sent" | "pending";
  invoices?: IBillingCustomerInvoice[];
}

export interface IBillingCustomerBillingRound {
  start_date: string;
  end_date: string;
  display_text: string;
}

export interface IBillingCustomersRoundSummary {
  total_customers: number;
  total_invoices: number;
  paid_invoices: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
}

export interface IBillingCustomersQueryParams {
  start_date: string;
  end_date: string;
  customer_code?: string;
  search?: string;
  status?: "paid" | "pending" | "partial";
  page?: number;
  limit?: number;
  enforce_customer_scope?: boolean;
}

export interface IBillingCustomersResponse {
  status: boolean;
  data: {
    billing_round: IBillingCustomerBillingRound;
    customers: IBillingCustomer[];
    summary: IBillingCustomersRoundSummary;
    pagination: IPagination;
  };
}

// ========== Customer Invoices (per customer detail) ==========

export interface IBillingInvoicesQueryParams {
  customer_code: string;
  start_date: string;
  end_date: string;
  search?: string;
  payment_status?: "paid" | "partial" | "pending";
  page?: number;
  limit?: number;
  enforce_customer_scope?: boolean;
}

export interface IBillingInvoicesResponse {
  status: boolean;
  data: {
    customer: {
      customer_code: string;
      customer_name: string;
      customer_email?: string;
      customer_folder_url?: string | null;
    };
    billing_round?: IBillingCustomerBillingRound;
    invoices: IBillingCustomerInvoice[];
    summary: {
      total_invoices: number;
      paid_invoices?: number;
      total_amount: number;
      paid_amount: number;
      outstanding_amount: number;
      billing_file_count?: number;
      email_sent?: boolean;
    };
    pagination: IPagination;
  };
}

// ========== Payment ==========

export interface IPaymentInvoiceAllocation {
  uuid?: string;
  payment_uuid?: string;
  invoice_uuid: string;
  invoice_number: string;
  allocated_amount: number;
  total_amount?: number;
  this_payment?: number;
  payment_status?: string;
}

export interface IPaymentEvidence {
  uuid?: string;
  file_uuid?: string;
  payment_uuid?: string;
  file_name: string;
  file_url: string;
  thumbnail_url: string | null;
  file_size?: number;
  file_type?: string;
  transfer_amount?: number | string | null;
  transfer_date?: string | null;
  transfer_reference?: string | null;
  notes?: string | null;
  uploaded_by?: string | null;
  source?: "admin" | "customer" | "portal" | string | null;
  verified_by?: string | null;
  verified_at?: string | null;
  ocr_status?: string | null;
  ocr_result?: Record<string, any> | null;
  ocr_confidence?: number | string | null;
  created_at?: string;
  uploaded_at?: string;
}

export interface IPaymentVerificationSummary {
  payment_amount: number;
  evidence_file_count: number;
  evidence_with_amount_count: number;
  evidence_amount_total: number | null;
  amount_difference: number | null;
  amount_matches_payment: boolean | null;
  missing_amount_count: number;
  ocr_status_counts: Record<string, number>;
  ocr_confidence_min: number | null;
  ocr_confidence_max: number | null;
  warnings: string[];
}

export type TBillingPaymentReviewStatus =
  | "submitted"
  | "approved"
  | "rejected"
  | "confirmed"
  | "completed"
  | "pending"
  | "cancelled";

export interface IBillingPayment {
  uuid?: string;
  payment_uuid?: string;
  payment_id: string; // PAY-YYMMDD-NNNN
  billing_round_uuid?: string | null;
  billing_round?: string;
  customer_code: string;
  customer_name?: string;
  total_paid_amount: number;
  payment_method: TPaymentMethod;
  payment_date: string; // YYYY-MM-DD
  payment_reference: string | null;
  bank_name: string | null;
  bank_account: string | null;
  notes: string | null;
  status: TBillingPaymentReviewStatus;
  approved_by?: string | null;
  approved_at?: string | null;
  revision?: string;
  audit_events?: Array<{ action: string; actor?: string; at?: string; reason?: string; request_id?: string; result_uuid?: string; result_id?: string; payment_uuid?: string; payment_id?: string; mode?: string; before?: { amount?: number; date?: string; method?: string; reference?: string; notes?: string }; after?: { amount?: number; date?: string; method?: string; reference?: string; notes?: string } }>;
  archived_evidence?: Array<IPaymentEvidence & { archived_by?: string; archived_at?: string }>;
  rejected_by?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  cancelled_by?: string | null;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  recorded_by: string | null;
  recorded_at?: string;
  franchise_uuid: string | null;
  created_at?: string;
  updated_at?: string;
  invoice_count?: number;
  invoiceAllocations?: IPaymentInvoiceAllocation[];
  evidenceFiles?: IPaymentEvidence[];
  invoice_payments?: IPaymentInvoiceAllocation[];
  evidence_files?: IPaymentEvidence[];
  verification_summary?: IPaymentVerificationSummary;
}

export type TPaymentMethod = "cash" | "transfer" | "cheque" | "credit_card" | "other";

// ========== Create Payment ==========

export interface ICreatePaymentAllocation {
  invoice_uuid: string;
  amount: number;
}

export interface ICreatePaymentEvidenceMetadata {
  file_name?: string;
  transfer_amount?: number;
  transfer_date?: string;
  transfer_reference?: string;
  notes?: string;
}

export interface ICreatePaymentDto {
  expected_invoice_balances?: Array<{ invoice_uuid: string; outstanding_amount: number }>;
  client_request_id?: string;
  invoice_uuids: string[];
  billing_round_uuid?: string;
  billing_round?: string;
  customer_code: string;
  total_paid_amount: number;
  payment_method: TPaymentMethod;
  payment_date: string; // YYYY-MM-DD
  payment_reference?: string;
  bank_name?: string;
  bank_account?: string;
  notes?: string;
  evidence_source?: "admin" | "customer" | "portal";
  enforce_customer_scope?: boolean;
  allocation?: ICreatePaymentAllocation[];
  evidence_metadata?: ICreatePaymentEvidenceMetadata[];
  files?: File[];
}

export interface ICreatePaymentResponse {
  status: boolean;
  message: string;
  data: {
    uuid?: string;
    payment_uuid?: string;
    payment_id: string;
    billing_round?: string;
    customer_code: string;
    total_paid_amount: number;
    payment_method: string;
    payment_date: string;
    payment_reference: string | null;
    bank_name?: string | null;
    status: string;
    review_status?: string;
    invoices_updated?: number;
    evidence_files?: number | IPaymentEvidence[];
    invoice_payments?: IPaymentInvoiceAllocation[];
    verification_summary?: IPaymentVerificationSummary;
    allocation?: IPaymentInvoiceAllocation[];
  };
}

// ========== Update Payment ==========

export interface IUpdatePaymentDto {
  invoice_uuids?: string[];
  total_paid_amount?: number;
  payment_method?: TPaymentMethod;
  payment_date?: string;
  payment_reference?: string;
  bank_name?: string;
  bank_account?: string;
  notes?: string;
  allocation?: ICreatePaymentAllocation[];
  remove_file_uuids?: string[];
  files?: File[];
}

export interface IUpdatePaymentResponse {
  status: boolean;
  message: string;
  data: {
    uuid: string;
    payment_id: string;
    total_paid_amount: number;
    status: string;
    invoices_updated: number;
    files_removed: number;
    files_added: number;
  };
}

export interface IUpdatePaymentEvidenceVerificationDto {
  transfer_amount?: number;
  transfer_date?: string;
  transfer_reference?: string;
  notes?: string;
  ocr_status?: "not_requested" | "processing" | "completed" | "failed" | "manual_review";
  ocr_result?: Record<string, any>;
  ocr_confidence?: number;
}

export interface IUpdatePaymentEvidenceVerificationResponse {
  status: boolean;
  message: string;
  data: {
    payment_uuid: string;
    evidence: IPaymentEvidence;
    verification_summary: IPaymentVerificationSummary;
  };
}

// ========== Delete Payment ==========

export interface ICancelBillingPaymentDto {
  cancellation_reason?: string;
}

export interface IDeletePaymentResponse {
  status: boolean;
  message: string;
  data: {
    uuid?: string;
    payment_uuid?: string;
    payment_id?: string;
    cancelled_by?: string | null;
    cancellation_reason?: string | null;
    invoices_reverted?: number;
    reverted_invoices?: IPaymentInvoiceAllocation[];
  };
}

// ========== Get Payments ==========

export interface IBillingPaymentsQueryParams {
  client_request_id?: string;
  status?: TBillingPaymentReviewStatus;
  billing_round_uuid?: string;
  customer_code?: string;
  billing_round?: string;
  invoice_uuid?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
  enforce_customer_scope?: boolean;
}

export interface IBillingPaymentsResponse {
  status: boolean;
  data: {
    payments: IBillingPayment[];
    summary?: IBillingPaymentsSummary;
    pagination: IPagination;
  };
}

export interface IBillingPaymentDetailResponse {
  status: boolean;
  data: IBillingPayment;
}

export interface IApproveBillingPaymentResponse {
  status: boolean;
  message: string;
  data: {
    payment_uuid: string;
    payment_status: "approved";
    approved_by: string;
    invoice_payments: IPaymentInvoiceAllocation[];
  };
}

export interface IRejectBillingPaymentDto {
  rejection_reason?: string;
}

export interface IRejectBillingPaymentResponse {
  status: boolean;
  message: string;
  data: {
    payment_uuid: string;
    payment_status: "rejected";
    rejected_by: string;
    rejected_at?: string | null;
    rejection_reason?: string | null;
  };
}

// ========== Upload Evidence ==========

export interface IUploadEvidenceResponse {
  status: boolean;
  message: string;
  data: {
    payment_uuid: string;
    files_uploaded: number;
    files: IPaymentEvidence[];
  };
}

export interface IUploadPaymentEvidenceOptions {
  evidence_source?: "admin" | "customer" | "portal";
  enforce_customer_scope?: boolean;
}

// ========== Pagination ==========

export interface IPagination {
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  totalPages?: number;
}

// ========== Default Values ==========

export const DBillingRoundsQueryParams: IBillingRoundsQueryParams = {
  page: 1,
  limit: 20,
};

export const DBillingCustomersQueryParams: IBillingCustomersQueryParams = {
  start_date: "",
  end_date: "",
  page: 1,
  limit: 50,
};

// ========== Billing Round Management ==========

export interface ICreateBillingRoundDto {
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  round_label?: string;
  customer_code?: string;
  notes?: string;
}

export interface IUpdateBillingRoundDto {
  round_label?: string;
  status?: "pending" | "completed";
  notes?: string;
}

export interface IRegenerateBillingRoundsDto {
  dry_run?: boolean;
  year?: string;
}

export interface IClearRoundsResponse {
  message: string;
  cleared_count: number;
  skipped_count: number;
}

export interface IRegeneratePreviewItem {
  action: "clear" | "skip";
  round_label: string;
  reason?: string;
}

export interface IRegenerateReadinessSummary {
  removable_rounds: number;
  protected_rounds: number;
  predicted_new_rounds: number;
  default_cycle_rounds: number;
  custom_cycle_rounds: number;
  customers_with_custom_cycle: number;
  customers_with_manual_cycle: number;
  customers_with_missing_template: number;
}

export interface IRegenerateRoundsResponse {
  message: string;
  dry_run: boolean;
  cleared_count: number;
  skipped_count: number;
  created_count: number;
  readiness_summary?: IRegenerateReadinessSummary;
  preview?: IRegeneratePreviewItem[];
}

// ========== Billing Periods (server-side grouped) ==========

export interface IBillingPeriod {
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  period_label: string; // e.g. "1/4/2569 - 15/4/2569"
  round_count: number; // total rounds in this period
  customer_count: number; // # of per-customer rounds
  global_round_count: number;
  pending_count: number;
  completed_count: number;
  has_unpaid: boolean;
  can_delete: boolean;
  invoice_count: number; // จำนวนใบกำกับภาษี
  total_amount: number; // รวมยอดบิลทั้งหมดในรอบนี้
  outstanding_amount: number; // ยอดค้างชำระในรอบนี้
}

export interface IBillingPeriodsSummary {
  total_periods: number;
  total_rounds: number;
  pending_rounds: number;
  completed_rounds: number;
  total_amount: number;
  outstanding_amount: number;
}

export interface IBillingPeriodsQueryParams {
  year?: string;
  month?: number; // 0 = all months, 1-12 = specific month
  status?: "pending" | "completed" | "all";
  enforce_customer_scope?: boolean;
}

export interface IBillingPeriodsResponse {
  status: boolean;
  data: {
    periods: IBillingPeriod[];
    summary: IBillingPeriodsSummary;
  };
}

export interface IPeriodRound {
  id: string;
  round_label: string;
  start_date: string;
  end_date: string;
  round_type: "global" | "per_customer";
  customer_code: string | null;
  customer_name: string | null;
  billing_cycle_template_uuid: string | null;
  billing_cycle_type: BillingCycleType | null;
  billing_cycle_snapshot: IBillingCycleSnapshot | null;
  status: "pending" | "completed";
  payment_count: number;
  bill_count: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
}

export interface IPeriodRoundsResponse {
  status: boolean;
  data: {
    rounds: IPeriodRound[];
  };
}

// ========== Customers Billing Summary (cross-period) ==========

export interface IBillingCustomerSummary {
  customer_code: string;
  customer_name: string | null;
  total_rounds: number;
  pending_rounds: number;
  completed_rounds: number;
  invoice_count: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
}

export interface IBillingCustomersSummaryResponse {
  status: boolean;
  data: {
    customers: IBillingCustomerSummary[];
    total: number;
  };
}

export interface IBillingCustomersSummaryQueryParams {
  year?: string;
  month?: number;
  search?: string;
  status_filter?: "all" | "has_outstanding" | "completed";
  enforce_customer_scope?: boolean;
}

export const DCreatePaymentDto: ICreatePaymentDto = {
  invoice_uuids: [],
  customer_code: "",
  total_paid_amount: 0,
  payment_method: "transfer",
  payment_date: "",
};

export interface IBillingPaymentsSummary {
  status_counts: Partial<Record<TBillingPaymentReviewStatus, number>>;
  approved_received_amount: number;
  pending_amount: number;
  rejected_amount: number;
  cancelled_amount: number;
  total_payments: number;
}
export interface IBillingReportParams {
  customer_code: string;
  start_date: string;
  end_date: string;
  billing_round_uuid?: string;
  status?: string;
}


export interface IPaymentCorrectionEvidence {
  uuid?: string;
  transfer_amount: number;
  transfer_date: string;
  transfer_reference: string;
  notes: string;
}
export interface ICorrectPaymentDto extends IUpdatePaymentDto {
  correction_mode: 'edit' | 'replace';
  client_request_id: string;
  expected_revision: string;
  correction_reason: string;
  invoice_uuids: string[];
  expected_invoice_balances: Array<{ invoice_uuid: string; outstanding_amount: number }>;
  existing_evidence: IPaymentCorrectionEvidence[];
  evidence_metadata: IPaymentCorrectionEvidence[];
}
export interface IPaymentCorrectionContext {
  payment: IBillingPayment;
  invoices: IBillingCustomerInvoice[];
  correction_version: number;
  replacing: boolean;
}
